/**
 * Agent-side notification tones. Same synthesis approach as the widget, so
 * neither app ships an audio file, but kept separate: the two bundles share no
 * runtime by design.
 *
 * Mute is a local preference. `isEmergency` on the message payload overrides
 * it — the server sends the flag precisely so the client can make that call.
 *
 * An agent may replace any of the four tones with a file of their own. Those
 * live in localStorage as data URLs, per browser rather than per account: a
 * notification sound is a property of the room you are sitting in, and syncing
 * one agent's air-horn to the laptop they use from a cafe helps nobody.
 */

let ctx = null;

function context() {
  const Ctor = window.AudioContext || window.webkitAudioContext;
  if (!Ctor) return null;
  if (!ctx) ctx = new Ctor();
  if (ctx.state === 'suspended') ctx.resume().catch(() => {});
  return ctx;
}

function tone(audio, { freq, at = 0, duration = 0.16, gain = 0.1 }) {
  const start = audio.currentTime + at;
  const osc = audio.createOscillator();
  const amp = audio.createGain();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(freq, start);
  amp.gain.setValueAtTime(0.0001, start);
  amp.gain.exponentialRampToValueAtTime(gain, start + 0.012);
  amp.gain.exponentialRampToValueAtTime(0.0001, start + duration);
  osc.connect(amp).connect(audio.destination);
  osc.start(start);
  osc.stop(start + duration + 0.02);
}

/* ------------------------------------------------------- custom sounds */

/**
 * The four things that can make a noise, and what each one is for.
 *
 * `message` and `emergency` are the long-standing pair. `visitorOnline` and
 * `chatOpened` exist because continuous alert mode rings on them too, and an
 * agent who wants to tell "someone landed on the page" apart from "someone
 * opened the chat" cannot do it if both play the same two notes.
 */
export const SOUND_TRIGGERS = /** @type {const} */ ([
  'message',
  'visitorOnline',
  'chatOpened',
  'emergency',
]);

export const SOUND_TRIGGER_LABELS = {
  message: 'New message',
  visitorOnline: 'Visitor online',
  chatOpened: 'Chat opened',
  emergency: 'User alert',
};

/**
 * Per-file ceiling for an uploaded tone.
 *
 * localStorage is a ~5MB budget shared with everything else the dashboard
 * keeps there, and base64 inflates a file by a third. Four sounds at this cap
 * is under a megabyte, which leaves the budget intact — and a notification
 * tone longer than a second or two is the wrong thing anyway.
 */
export const MAX_SOUND_BYTES = 128 * 1024;

const soundKey = (trigger) => `superchat:sound:${trigger}`;

export const customSound = (trigger) => localStorage.getItem(soundKey(trigger));

/** Every trigger that currently has a file behind it. */
export function customSounds() {
  return Object.fromEntries(
    SOUND_TRIGGERS.map((trigger) => [trigger, customSound(trigger)]).filter(([, v]) => v)
  );
}

/**
 * Stores an uploaded file against a trigger.
 *
 * Rejects rather than truncates: half a sound file is not a quieter sound
 * file, it is a decode error at the moment the agent needed to hear something.
 * A full quota is reported the same way, since it is the same outcome.
 */
export async function setCustomSound(trigger, file) {
  if (!SOUND_TRIGGERS.includes(trigger)) throw new Error(`Unknown sound trigger: ${trigger}`);
  if (!file.type.startsWith('audio/')) throw new Error('That file is not audio');
  if (file.size > MAX_SOUND_BYTES) {
    throw new Error(`Sound files must be under ${Math.round(MAX_SOUND_BYTES / 1024)} KB`);
  }

  const dataUrl = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error('Could not read that file'));
    reader.readAsDataURL(file);
  });

  try {
    localStorage.setItem(soundKey(trigger), dataUrl);
  } catch {
    throw new Error('No room left in this browser’s storage for another sound');
  }
  return dataUrl;
}

export function clearCustomSound(trigger) {
  localStorage.removeItem(soundKey(trigger));
}

/**
 * Plays an agent's own file for this trigger.
 *
 * @returns {boolean} whether it took responsibility for making the noise.
 * `false` means the caller should fall back to the synthesised tone — there
 * was no file, or the browser could not decode the one there was. A stored
 * file that fails to decode is dropped, so the failure is not repeated on
 * every message for the rest of the session.
 */
function playCustom(trigger) {
  const dataUrl = customSound(trigger);
  if (!dataUrl) return false;

  try {
    const player = new Audio(dataUrl);
    player.addEventListener('error', () => {
      console.warn(`[superchat] custom ${trigger} sound could not be decoded — reverting`);
      clearCustomSound(trigger);
    });
    // Autoplay can still be refused before the first gesture; the synthesised
    // path is no better off in that case, so there is nothing to fall back to.
    player.play().catch(() => {});
    return true;
  } catch {
    clearCustomSound(trigger);
    return false;
  }
}

/** Plays a stored sound on demand, for the preview button in settings. */
export function previewSound(trigger) {
  if (playCustom(trigger)) return;
  if (trigger === 'emergency') synthEmergency();
  else synthIncoming();
}

/* --------------------------------------------------------- synthesised */

function synthIncoming() {
  const audio = context();
  if (!audio) return;
  tone(audio, { freq: 587.33, gain: 0.08 });
  tone(audio, { freq: 783.99, at: 0.1, gain: 0.08 });
}

function synthEmergency() {
  const audio = context();
  if (!audio) return;
  for (let i = 0; i < 3; i += 1) {
    const at = i * 0.44;
    tone(audio, { freq: 987.77, at, duration: 0.2, gain: 0.24 });
    tone(audio, { freq: 739.99, at: at + 0.2, duration: 0.22, gain: 0.24 });
  }
}

/**
 * The tone for one trigger: the agent's file if they set one, ours otherwise.
 *
 * Every trigger except `emergency` falls back to the same two notes, which is
 * the behaviour these had before uploads existed — the new triggers are
 * distinguishable only once somebody actually uploads something for them.
 */
export function playFor(trigger) {
  if (playCustom(trigger)) return;
  if (trigger === 'emergency') synthEmergency();
  else synthIncoming();
}

export const playIncoming = () => playFor('message');
export const playEmergency = () => playFor('emergency');

/**
 * Alert mode: keeps ringing until the message is actually read.
 *
 * An unattended dashboard is the case this exists for, so the loop is driven
 * by *unread state*, not by a timeout — it stops when the agent reads, and
 * only then. Idempotent: calling start twice does not stack two loops.
 */
let ringTimer = null;
let ringingTrigger = null;

/**
 * `trigger` names which sound to loop, so continuous mode rings with the tone
 * belonging to whatever is actually waiting — an arrival keeps ringing as an
 * arrival rather than as a message that never came.
 */
export function startRinging({ emergency = false, trigger = null, intervalMs = 3000 } = {}) {
  const which = emergency ? 'emergency' : (trigger ?? 'message');
  // Already looping the right sound. Restarted when it is the wrong one, so an
  // emergency landing mid-ring is heard as an emergency.
  if (ringTimer && ringingTrigger === which) return;
  if (ringTimer) stopRinging();

  const ring = () => playFor(which);
  ringingTrigger = which;
  ring();
  ringTimer = setInterval(ring, emergency ? Math.min(intervalMs, 2000) : intervalMs);
}

export function stopRinging() {
  clearInterval(ringTimer);
  ringTimer = null;
  ringingTrigger = null;
}

export const isRinging = () => ringTimer !== null;

/** Browsers refuse to start an AudioContext without a gesture. */
export function primeAudio() {
  const audio = context();
  if (!audio) return;
  const source = audio.createBufferSource();
  source.buffer = audio.createBuffer(1, 1, 22050);
  source.connect(audio.destination);
  source.start(0);
}

export default {
  playIncoming,
  playEmergency,
  playFor,
  previewSound,
  primeAudio,
  startRinging,
  stopRinging,
  isRinging,
  SOUND_TRIGGERS,
  SOUND_TRIGGER_LABELS,
  customSounds,
  setCustomSound,
  clearCustomSound,
  MAX_SOUND_BYTES,
};
